import type { User, Achievement } from '../types/index';

export const getRankFromPoints = (points: number): User['rank'] => {
  if (points >= 50000) return 'legendary';
  if (points >= 25000) return 'campus-hero';
  if (points >= 10000) return 'green-champion';
  if (points >= 5000) return 'eco-warrior';
  if (points >= 2000) return 'cleaner';
  if (points >= 1000) return 'novice';
  if (points >= 500) return 'rookie';
  if (points >= 100) return 'newcomer';
  return 'beginner';
};

export const getRankInfo = (rank: User['rank']) => {
  const ranks = {
    'beginner': { name: 'Beginner', icon: '🌱', color: '#10b981', minPoints: 0 },
    'newcomer': { name: 'Newcomer', icon: '🌿', color: '#06b6d4', minPoints: 100 },
    'rookie': { name: 'Rookie', icon: '🧹', color: '#3b82f6', minPoints: 500 },
    'novice': { name: 'Novice', icon: '🍃', color: '#6366f1', minPoints: 1000 },
    'cleaner': { name: 'Cleaner', icon: '🧽', color: '#8b5cf6', minPoints: 2000 },
    'eco-warrior': { name: 'Eco Warrior', icon: '♻️', color: '#a855f7', minPoints: 5000 },
    'green-champion': { name: 'Green Champion', icon: '🌟', color: '#f59e0b', minPoints: 10000 },
    'campus-hero': { name: 'Campus Hero', icon: '🏆', color: '#ef4444', minPoints: 25000 },
    'legendary': { name: 'Legendary', icon: '👑', color: '#ec4899', minPoints: 50000 }
  };
  return ranks[rank] || ranks.beginner;
};

export const getNextRankProgress = (points: number, currentRank: User['rank']) => {
  const rankThresholds = [100, 500, 1000, 2000, 5000, 10000, 25000, 50000];
  const rankOrder = ['beginner', 'newcomer', 'rookie', 'novice', 'cleaner', 'eco-warrior', 'green-champion', 'campus-hero', 'legendary'];
  const currentRankIndex = rankOrder.indexOf(currentRank);
  
  if (currentRankIndex === 8) return { progress: 100, pointsNeeded: 0 };
  
  const nextThreshold = rankThresholds[currentRankIndex];
  const prevThreshold = currentRankIndex === 0 ? 0 : rankThresholds[currentRankIndex - 1];
  
  const progress = ((points - prevThreshold) / (nextThreshold - prevThreshold)) * 100;
  const pointsNeeded = nextThreshold - points;
  
  return { progress: Math.min(100, Math.max(0, progress)), pointsNeeded };
};


export const getAllAchievements = (): Achievement[] => [
  // Activity achievements
  { id: 'first-steps', name: 'First Steps', description: 'Complete your first cleaning activity', icon: '🎯', category: 'activity' },
  { id: 'perfectionist', name: 'Perfectionist', description: 'Complete 5 before/after submissions', icon: '📸', category: 'activity' },
  { id: 'video-star', name: 'Video Star', description: 'Complete 5 video submissions', icon: '🎬', category: 'activity' },
  { id: 'early-bird', name: 'Early Bird', description: 'Clean before 8 AM', icon: '🌅', category: 'activity' },
  { id: 'weekend-warrior', name: 'Weekend Warrior', description: 'Clean on Saturday or Sunday', icon: '💪', category: 'activity' },
  { id: 'night-owl', name: 'Night Owl', description: 'Clean after 8 PM', icon: '🦉', category: 'activity' },
  { id: 'marathon-cleaner', name: 'Marathon Cleaner', description: 'Complete 10 activities in a single day', icon: '🏃', category: 'activity' },
  { id: 'consistency-king', name: 'Consistency King', description: 'Clean at least once every day for a week', icon: '👑', category: 'activity' },
  
  // Milestone achievements
  { id: '50-club', name: '50 Club', description: 'Complete 50 total cleaning activities', icon: '🌟', category: 'milestone' },
  { id: 'centurion', name: 'Centurion', description: 'Complete 100 total cleaning activities', icon: '💯', category: 'milestone' },
  { id: 'veteran', name: 'Veteran', description: 'Be active for 6 months', icon: '🎖️', category: 'milestone' },
  
  // Streak achievements
  { id: 'week-streak', name: 'Week Warrior', description: '7 day cleaning streak', icon: '🔥', category: 'streak' },
  { id: 'month-streak', name: 'Monthly Master', description: '30 day cleaning streak', icon: '🏅', category: 'streak' },
  { id: 'century-streak', name: 'Century Club', description: '100 day cleaning streak', icon: '💯', category: 'streak' },
  
  // Location achievements
  { id: 'explorer', name: 'Explorer', description: 'Clean in 5 different locations', icon: '🗺️', category: 'location' },
  { id: 'campus-explorer', name: 'Campus Explorer', description: 'Clean in 10 different locations', icon: '🧭', category: 'location' },
  
  // Social achievements
  { id: 'popular', name: 'Popular', description: 'Get 100 total reactions', icon: '❤️', category: 'social' },
  { id: 'influencer', name: 'Influencer', description: 'Get 50 reactions on a single activity', icon: '🌟', category: 'social' },
  { id: 'communicator', name: 'Communicator', description: 'Leave 10 comments', icon: '💬', category: 'social' },
  { id: 'motivator', name: 'Motivator', description: 'Have 5 posts get 20+ reactions', icon: '💪', category: 'social' },
  
  // Special achievements
  { id: 'dedication', name: 'Dedication', description: 'Login 30 days in a row', icon: '📅', category: 'special' }
];

export const checkAchievements = (user: User, action?: { type: string; data?: any }): Achievement[] => {
  const allAchievements = getAllAchievements();
  const newAchievements: Achievement[] = [];
  
  // Check First Steps
  if (user.totalActivities >= 1 && !user.achievements.find(a => a.id === 'first-steps')) {
    newAchievements.push({ ...allAchievements.find(a => a.id === 'first-steps')!, unlockedAt: new Date() });
  }
  
  // Check Perfectionist
  if (user.beforeAfterCount >= 5 && !user.achievements.find(a => a.id === 'perfectionist')) {
    newAchievements.push({ ...allAchievements.find(a => a.id === 'perfectionist')!, unlockedAt: new Date() });
  }
  
  // Check Video Star
  if (user.videoCount >= 5 && !user.achievements.find(a => a.id === 'video-star')) {
    newAchievements.push({ ...allAchievements.find(a => a.id === 'video-star')!, unlockedAt: new Date() });
  }
  
  // Check streak achievements
  if (user.streak >= 7 && !user.achievements.find(a => a.id === 'week-streak')) {
    newAchievements.push({ ...allAchievements.find(a => a.id === 'week-streak')!, unlockedAt: new Date() });
  }
  
  if (user.streak >= 30 && !user.achievements.find(a => a.id === 'month-streak')) {
    newAchievements.push({ ...allAchievements.find(a => a.id === 'month-streak')!, unlockedAt: new Date() });
  }
  
  if (user.streak >= 100 && !user.achievements.find(a => a.id === 'century-streak')) {
    newAchievements.push({ ...allAchievements.find(a => a.id === 'century-streak')!, unlockedAt: new Date() });
  }
  
  // Check login streak
  if (user.loginStreak >= 30 && !user.achievements.find(a => a.id === 'dedication')) {
    newAchievements.push({ ...allAchievements.find(a => a.id === 'dedication')!, unlockedAt: new Date() });
  }
  
  // Check milestone achievements
  if (user.totalActivities >= 50 && !user.achievements.find(a => a.id === '50-club')) {
    newAchievements.push({ ...allAchievements.find(a => a.id === '50-club')!, unlockedAt: new Date() });
  }
  
  if (user.totalActivities >= 100 && !user.achievements.find(a => a.id === 'centurion')) {
    newAchievements.push({ ...allAchievements.find(a => a.id === 'centurion')!, unlockedAt: new Date() });
  }
  
  // Time-based achievements (if action provided)
  if (action?.type === 'activity') {
    const hour = new Date().getHours();
    const day = new Date().getDay();
    
    if (hour < 8 && !user.achievements.find(a => a.id === 'early-bird')) {
      newAchievements.push({ ...allAchievements.find(a => a.id === 'early-bird')!, unlockedAt: new Date() });
    }
    
    if (hour >= 20 && !user.achievements.find(a => a.id === 'night-owl')) {
      newAchievements.push({ ...allAchievements.find(a => a.id === 'night-owl')!, unlockedAt: new Date() });
    }
    
    if ((day === 0 || day === 6) && !user.achievements.find(a => a.id === 'weekend-warrior')) {
      newAchievements.push({ ...allAchievements.find(a => a.id === 'weekend-warrior')!, unlockedAt: new Date() });
    }
    
    // Check marathon cleaner (10 activities in a day)
    if ((user.dailyActivityCount || 0) >= 10 && !user.achievements.find(a => a.id === 'marathon-cleaner')) {
      newAchievements.push({ ...allAchievements.find(a => a.id === 'marathon-cleaner')!, unlockedAt: new Date() });
    }
    
    // Check consistency king (7 consecutive days)
    if ((user.consecutiveActivityDays || 0) >= 7 && !user.achievements.find(a => a.id === 'consistency-king')) {
      newAchievements.push({ ...allAchievements.find(a => a.id === 'consistency-king')!, unlockedAt: new Date() });
    }
    
    // Check location achievements
    const uniqueLocationCount = user.uniqueLocations?.length || 0;
    if (uniqueLocationCount >= 5 && !user.achievements.find(a => a.id === 'explorer')) {
      newAchievements.push({ ...allAchievements.find(a => a.id === 'explorer')!, unlockedAt: new Date() });
    }
    
    if (uniqueLocationCount >= 10 && !user.achievements.find(a => a.id === 'campus-explorer')) {
      newAchievements.push({ ...allAchievements.find(a => a.id === 'campus-explorer')!, unlockedAt: new Date() });
    }
  }
  
  // Check veteran achievement (6 months)
  if (user.joinedAt) {
    const joinDate = new Date(user.joinedAt);
    const monthsSinceJoined = (new Date().getTime() - joinDate.getTime()) / (1000 * 60 * 60 * 24 * 30);
    if (monthsSinceJoined >= 6 && !user.achievements.find(a => a.id === 'veteran')) {
      newAchievements.push({ ...allAchievements.find(a => a.id === 'veteran')!, unlockedAt: new Date() });
    }
  }
  
  // Check social achievements
  // Communicator - Leave 10 comments
  if ((user.commentCount || 0) >= 10 && !user.achievements.find(a => a.id === 'communicator')) {
    newAchievements.push({ ...allAchievements.find(a => a.id === 'communicator')!, unlockedAt: new Date() });
  }
  
  // Popular - Get 100 total reactions
  if ((user.totalReactionsReceived || 0) >= 100 && !user.achievements.find(a => a.id === 'popular')) {
    newAchievements.push({ ...allAchievements.find(a => a.id === 'popular')!, unlockedAt: new Date() });
  }
  
  // Influencer - Get 50 reactions on a single activity
  if ((user.maxReactionsOnSinglePost || 0) >= 50 && !user.achievements.find(a => a.id === 'influencer')) {
    newAchievements.push({ ...allAchievements.find(a => a.id === 'influencer')!, unlockedAt: new Date() });
  }
  
  // Motivator - Have 5 posts get 20+ reactions
  if ((user.postsWithHighReactions || 0) >= 5 && !user.achievements.find(a => a.id === 'motivator')) {
    newAchievements.push({ ...allAchievements.find(a => a.id === 'motivator')!, unlockedAt: new Date() });
  }
  
  return newAchievements;
};